import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  Icon,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  VStack,
} from '@chakra-ui/react';
import { Form, Formik } from 'formik';
import * as _ from 'lodash';
import React, { useState } from 'react';
import * as Yup from 'yup';

import {
  finleyAssert,
  utils as commonUtils,
} from '@finley-technologies/common';
import { DbTable } from '@finley-technologies/finley-types';

import {
  SearchSelectFormField,
  SelectFormField,
  TextInputFormField,
} from 'packages/common-frontend/components/FormField';
import { GET_CREDIT_FACILITIES } from 'packages/credit-facility/requests/requests.gql';
import { useCreateCreditFacilityMutation } from 'packages/credit-facility/requests/requests.gql.generated';
import { FiPlusCircle } from 'react-icons/fi';
import { GET_ORGANIZATION_IMPLEMENTATIONS_DETAILS } from '~/packages/implementations/requests/implementations.gql';
import {
  VisibleOrganizationsQueryGQLType,
  GetOrganizationImplementationsDetailsQueryGQLType,
} from '~/packages/implementations/requests/implementations.gql.generated';

// Type alias for implementation from the client query
type OrganizationImplementationFragmentGQLType = GetOrganizationImplementationsDetailsQueryGQLType['organization']['implementations'][0];

// Type for organizations
type OrganizationType = VisibleOrganizationsQueryGQLType['organization']['visibleOrganizations'][0];

// Constants
const facilityTypeOptions = DbTable.FacilityTypeIterable.map((key: string) => ({
  label: _.startCase(key),
  value: key,
}));

const validationSchema = Yup.object().shape({
  slug: Yup.string().trim().strict().required(),
  name: Yup.string().min(5).trim().strict().required(),
  type: Yup.string()
    .oneOf([...DbTable.FacilityTypeIterable])
    .required(),
  capitalProviderId: Yup.string().uuid().required(),
  capitalProviderRepresentativeId: Yup.string().uuid().required(),
});

// Helpers
/** Creates a formik option from an organization. */
const createOrgOption = ({
  name,
  id,
  slug,
}: {
  name: string;
  id: string;
  slug: string;
}) => ({
  label: name,
  value: id,
  slug,
});

// !TODO @eldarshamukhamedov Slugs should be generated on the server, not on the client
/**
 * helper method to generate a creditFacilitySlug of the format [borrowerSlug][CapitalProviderSlug]1,
 * e.g. `acmeCorpMintPlazaBank1`
 * Note: Backend will handle uniqueness validation and increment the number if needed.
 * @param borrowerSlug string
 * @param capitalProviderSlug string
 * @returns `creditFacilitySlug` base format (backend will ensure uniqueness)
 */
const generateCreditFacilitySlug = ({
  borrowerSlug,
  capitalProviderSlug,
}: {
  borrowerSlug: string;
  capitalProviderSlug: string;
}): string => {
  const baseSlug =
    borrowerSlug + commonUtils.toInitialCase(capitalProviderSlug);
  return `${baseSlug}1`;
};

// Fragments
type AddCreditFacilityFormProps = {
  implementation: OrganizationImplementationFragmentGQLType;
  organizations: OrganizationType[];
  onSuccess: () => void;
};
const AddCreditFacilityForm = ({
  implementation,
  organizations,
  onSuccess,
}: AddCreditFacilityFormProps) => {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [createCreditFacility] = useCreateCreditFacilityMutation({
    refetchQueries: [
      {
        query: GET_CREDIT_FACILITIES,
        variables: { organizationId: implementation.borrowerOrganization.id },
      },
      {
        query: GET_ORGANIZATION_IMPLEMENTATIONS_DETAILS,
        variables: { organizationId: implementation.ownerOrganization.id },
      },
    ],
    awaitRefetchQueries: true,
  });

  const capitalProviderOptions = [...organizations]
    .sort((a, b) => a.name.localeCompare(b.name))
    .map(createOrgOption);

  const capitalProviderRepresentativeOptions = [...organizations]
    .sort((a, b) => a.name.localeCompare(b.name))
    .map(createOrgOption);

  if (capitalProviderOptions.length === 0) {
    return (
      <Box>
        <Alert status="warning">
          <AlertIcon />
          <AlertDescription>
            No organizations available. Cannot create credit facility.
          </AlertDescription>
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Formik
        initialValues={{
          slug: generateCreditFacilitySlug({
            borrowerSlug: implementation.borrowerOrganization.slug,
            capitalProviderSlug: capitalProviderOptions[0].slug, // this is an unsafe for bootstrapping
          }),
          name: '',
          capitalProviderId: capitalProviderOptions[0].value,
          // By default, use the capital provider as the rep
          capitalProviderRepresentativeId: capitalProviderOptions[0].value,
          type: 'creditFacility' as const,
        }}
        validationSchema={validationSchema}
        onSubmit={async ({
          slug,
          name,
          capitalProviderId,
          capitalProviderRepresentativeId,
          type,
        }) => {
          setErrorMessage(null);
          try {
            await createCreditFacility({
              variables: {
                slug,
                name,
                implementationId: implementation.id,
                capitalProviderIds: [capitalProviderId],
                capitalProviderRepresentativeIds: [
                  capitalProviderRepresentativeId,
                ],
                type,
              },
            });
            onSuccess();
          } catch (err) {
            // Surface the actual server error (e.g. credit-agreement
            // validation failures) instead of failing silently.
            setErrorMessage(
              err instanceof Error
                ? err.message
                : 'Failed to create credit facility.',
            );
          }
        }}
      >
        {(formik) => (
          <Form onSubmit={formik.handleSubmit}>
            <VStack spacing="10px">
              <TextInputFormField
                label={<>Facility Slug</>}
                name="slug"
                formatChange={(newValue) => _.camelCase(newValue)}
              />
              <TextInputFormField
                label="Facility Name"
                name="name"
                placeholder="Name the facility"
              />
              <SelectFormField
                label="Facility Type"
                name="type"
                options={facilityTypeOptions}
              />
              <SearchSelectFormField
                label="Capital Provider"
                name="capitalProviderId"
                options={capitalProviderOptions}
                onChange={(newValue) => {
                  // By default, use the capital provider as the rep
                  // TODO: would be nice to visually highlight the automatically updated field
                  formik.setFieldValue(
                    'capitalProviderRepresentativeId',
                    newValue,
                  );

                  const capitalProviderSlug = capitalProviderOptions.find(
                    (provider) => provider.value === newValue,
                  )?.slug;

                  finleyAssert(
                    capitalProviderSlug,
                    `could not find slug for capital provider id: ${newValue}`,
                  );

                  const creditFacilitySlug = generateCreditFacilitySlug({
                    borrowerSlug: implementation.borrowerOrganization.slug,
                    capitalProviderSlug,
                  });

                  formik.setFieldValue('slug', creditFacilitySlug);
                }}
                placeholder="Search by Capital Provider"
              />
              <SearchSelectFormField
                label="Capital Provider Representative"
                name="capitalProviderRepresentativeId"
                placeholder="Search by Representative"
                options={capitalProviderRepresentativeOptions}
              />
              {errorMessage && (
                <Alert status="error" mt="1rem" borderRadius="md">
                  <AlertIcon />
                  <AlertDescription whiteSpace="pre-wrap" fontSize="sm">
                    {errorMessage}
                  </AlertDescription>
                </Alert>
              )}
              <Button mt="1rem" type="submit" isDisabled={formik.isSubmitting}>
                Add Credit Facility
              </Button>
            </VStack>
          </Form>
        )}
      </Formik>
    </Box>
  );
};

// Exports
type AddCreditFacilityButtonProps = {
  implementation: OrganizationImplementationFragmentGQLType;
  organizations: OrganizationType[];
};
export const AddCreditFacilityButton = ({
  implementation,
  organizations,
}: AddCreditFacilityButtonProps) => {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <>
      <Button onClick={() => setIsOpen(true)} variant="outline">
        Add Credit Facility <Icon marginLeft="50px" as={FiPlusCircle} />
      </Button>

      <Modal isOpen={isOpen} onClose={() => setIsOpen(false)}>
        <ModalOverlay />
        <ModalContent padding="20px">
          <ModalHeader>Add Credit Facility</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <AddCreditFacilityForm
              implementation={implementation}
              organizations={organizations}
              onSuccess={() => setIsOpen(false)}
            />
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};
